/**
 * bannerSurface — live wiring for the banner-surface gate.
 *
 * The RULE itself (and the full rationale for replacing the old route
 * allowlist) lives in bannerSurfacePolicy.ts, which stays free of React,
 * router and firebase imports so it can be read and tested on its own. This
 * module only answers it for the current viewer and route.
 */

import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import { isBannerSurface } from './bannerSurfacePolicy';

export {
  BANNER_SUPPRESSED_PREFIXES,
  isBannerSuppressedPath,
  isBannerSurface,
} from './bannerSurfacePolicy';

/**
 * Live "may this band speak here" for the current viewer and route.
 *
 * Reads Firebase auth directly (rather than AuthContext) so a source keeps
 * working when mounted outside the app's providers — the same pattern the
 * verify banners already use. Anonymous sessions count as signed OUT.
 * Anything unexpected (no Firebase app, as in tests or SSR) fails CLOSED:
 * the band stays silent rather than guessing a viewer into existence.
 */
export function useBannerSurface(): boolean {
  const { pathname } = useLocation();
  const [signedIn, setSignedIn] = useState<boolean>(() => {
    try {
      const u = getAuth().currentUser;
      return !!u && !u.isAnonymous;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      return onAuthStateChanged(getAuth(), (u) => setSignedIn(!!u && !u.isAnonymous));
    } catch {
      setSignedIn(false);
      return undefined;
    }
  }, []);

  return isBannerSurface(signedIn, pathname);
}
